import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface DatePickerPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  selectedDate?: string | Date | null;
  onDateSelect?: (dateString: string) => void;
  isBulkUpdate?: boolean;
  onBulkDateSelect?: (dateString: string) => void;
  isBulkOperation?: boolean;
  showAbove?: boolean;
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  transformOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  width?: string | number;
  maxWidth?: string;
  showQuickOptions?: boolean;
}

export const DatePickerPopover: React.FC<DatePickerPopoverProps> = ({
  anchorEl,
  onClose,
  selectedDate,
  onDateSelect,
  isBulkUpdate = false,
  onBulkDateSelect,
  isBulkOperation = false,
  showAbove = false,
  anchorOrigin,
  transformOrigin,
  width,
  maxWidth,
  showQuickOptions = true,
}) => {
  const { t } = useTranslation();
  const today = new Date();

  // Helper function to parse date safely
  const parseDate = (date: string | Date | null | undefined): Date | null => {
    if (!date) return null;

    if (date instanceof Date) {
      return date;
    }

    if (typeof date === "string") {
      // Handle different string formats
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return null;
  };

  // Helper function to compare dates (ignoring time)
  const isSameDate = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const parsedSelectedDate = parseDate(selectedDate);

  const [month, setMonth] = useState(parsedSelectedDate || today);
  const [selectedDay, setSelectedDay] = useState(parsedSelectedDate);

  // Update selectedDay when selectedDate prop changes
  useEffect(() => {
    const newSelectedDate = parseDate(selectedDate);
    setSelectedDay(newSelectedDate);

    // Also update the month view if a valid date is selected
    if (newSelectedDate) {
      setMonth(newSelectedDate);
    }
  }, [selectedDate]);

  const handlePrevMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  };

  const days = [];
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);

  // Add previous month days to fill first week
  const firstDayOfWeek = firstDay.getDay();
  for (let i = firstDayOfWeek; i > 0; i--) {
    const prevMonthDay = new Date(month.getFullYear(), month.getMonth(), 1 - i);
    days.push({ date: prevMonthDay, isCurrentMonth: false });
  }

  // Add current month days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({
      date: new Date(month.getFullYear(), month.getMonth(), i),
      isCurrentMonth: true,
    });
  }

  // Add next month days to complete the grid
  const remainingDays = 7 - (days.length % 7 || 7);
  for (let i = 1; i <= remainingDays; i++) {
    const nextMonthDay = new Date(month.getFullYear(), month.getMonth() + 1, i);
    days.push({ date: nextMonthDay, isCurrentMonth: false });
  }

  const handleDayClick = (date: Date) => {
    setSelectedDay(date);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    if (isBulkUpdate && onBulkDateSelect) {
      onBulkDateSelect(formattedDate);
    } else if (onDateSelect) {
      onDateSelect(formattedDate);
    }

    onClose();
  };

  const quickOptions = [
    { label: t("Today"), date: today },
    { label: t("Later"), date: today },
    {
      label: t("Tomorrow"),
      date: new Date(today.getTime() + 24 * 60 * 60 * 1000),
    },
    {
      label: t("This weekend"),
      date: new Date(
        today.getTime() + ((6 - today.getDay()) % 7) * 24 * 60 * 60 * 1000
      ),
    },
    {
      label: t("Next week"),
      date: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000),
    },
    {
      label: t("Next weekend"),
      date: new Date(
        today.getTime() + (7 + ((6 - today.getDay()) % 7)) * 24 * 60 * 60 * 1000
      ),
    },
    {
      label: t("2 weeks"),
      date: new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000),
    },
    {
      label: t("4 weeks"),
      date: new Date(today.getTime() + 28 * 24 * 60 * 60 * 1000),
    },
  ];

  const isSelected = (date: Date) => {
    if (!selectedDay) return false;
    return isSameDate(date, selectedDay);
  };

  const weekdays = [
    t("Su"),
    t("Mo"),
    t("Tu"),
    t("We"),
    t("Th"),
    t("Fr"),
    t("Sa"),
  ];

  const monthNames = [
    t("January"),
    t("February"),
    t("March"),
    t("April"),
    t("May"),
    t("June"),
    t("July"),
    t("August"),
    t("September"),
    t("October"),
    t("November"),
    t("December"),
  ];

  const getAnchorOrigin = () => {
    if (anchorOrigin) return anchorOrigin;
    if (isBulkOperation) {
      return { vertical: "top" as const, horizontal: "center" as const };
    }
    if (showAbove) {
      return { vertical: "top" as const, horizontal: "center" as const };
    }
    return { vertical: "bottom" as const, horizontal: "center" as const };
  };

  const getTransformOrigin = () => {
    if (transformOrigin) return transformOrigin;
    if (isBulkOperation) {
      return { vertical: "bottom" as const, horizontal: "center" as const };
    }
    if (showAbove) {
      return { vertical: "bottom" as const, horizontal: "center" as const };
    }
    return { vertical: "top" as const, horizontal: "center" as const };
  };

  const popoverWidth = width || "450px";
  const popoverMaxWidth = maxWidth || "95vw";

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={getAnchorOrigin()}
      transformOrigin={getTransformOrigin()}
      sx={{
        "& .MuiPopover-paper": {
          width: popoverWidth,
          maxWidth: popoverMaxWidth,
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
          ...(isBulkOperation || showAbove ? { marginBottom: "8px" } : {}),
        },
      }}
    >
      <Box sx={{ display: "flex" }}>
        {showQuickOptions && (
          <Box sx={{ width: "40%", borderRight: "1px solid #eee", p: 1 }}>
            {quickOptions.map((option, index) => (
              <Box
                key={index}
                sx={{
                  py: 1,
                  px: 2,
                  cursor: "pointer",
                  borderRadius: "4px",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                  display: "flex",
                  justifyContent: "space-between",
                }}
                onClick={() => handleDayClick(option.date)}
              >
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {index === 1
                    ? "7:50 pm"
                    : index === 2
                    ? t("Wed")
                    : index === 3
                    ? t("Sat")
                    : index === 4
                    ? t("Mon")
                    : index === 5
                    ? t("31 May")
                    : index === 6
                    ? t("3 Jun")
                    : index === 7
                    ? t("17 Jun")
                    : t("Tue")}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            width: showQuickOptions ? "60%" : "100%",
            p: showQuickOptions ? 1 : 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <IconButton size="small" onClick={handlePrevMonth}>
              <KeyboardArrowDown sx={{ transform: "rotate(90deg)" }} />
            </IconButton>
            <Typography variant="subtitle2">
              {`${monthNames[month.getMonth()]} ${month.getFullYear()}`}
            </Typography>
            <IconButton size="small" onClick={handleNextMonth}>
              <KeyboardArrowDown sx={{ transform: "rotate(-90deg)" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 0.5,
            }}
          >
            {weekdays.map((day) => (
              <Typography
                key={day}
                variant="caption"
                align="center"
                sx={{ color: "text.secondary", py: 0.5 }}
              >
                {day}
              </Typography>
            ))}

            {days.map((day, index) => {
              const isSelectedDay = isSelected(day.date);

              return (
                <Box
                  key={index}
                  sx={{
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: "50%",
                    opacity: day.isCurrentMonth ? 1 : 0.5,
                    bgcolor: isSelectedDay ? "primary.main" : "transparent",
                    color: isSelectedDay ? "white" : "inherit",
                    "&:hover": {
                      bgcolor: isSelectedDay ? "primary.main" : "action.hover",
                    },
                  }}
                  onClick={() => handleDayClick(day.date)}
                >
                  <Typography variant="caption">
                    {day.date.getDate()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Popover>
  );
};
